import { useState } from "react";
import { Handle, Position } from "@xyflow/react";
import { DATA_TYPE_INFO } from "@/lib/registry/data-types";
import type { DataType } from "@/lib/types/module";
import { cn } from "@/lib/utils";

interface NodeHandleProps {
  name: string;
  label?: string;
  type: DataType;
  direction: "input" | "output";
  required?: boolean;
  showLabel?: boolean;
  disableHover?: boolean;
}

export function NodeHandle({
  name,
  label,
  type,
  direction,
  required,
  showLabel,
  disableHover,
}: NodeHandleProps) {
  const info = DATA_TYPE_INFO[type];
  const position = direction === "input" ? Position.Left : Position.Right;
  const isInput = direction === "input";
  const [hovered, setHovered] = useState(false);

  const visible = showLabel || (hovered && !disableHover);

  return (
    <div className="relative pointer-events-auto" style={{ height: 0 }}>
      <Handle
        type={isInput ? "target" : "source"}
        position={position}
        id={name}
        className="!absolute !w-2.5 !h-2.5 !rounded-full !border-2 !border-card"
        style={{
          background: info.color,
          [isInput ? "left" : "right"]: -5,
          top: "50%",
          transform: "translateY(-50%)",
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      />

      {/* Floating label — shows on hover (when not disabled) or when node is selected */}
      {visible && (
        <div
          className={cn(
            "absolute top-1/2 -translate-y-1/2 flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-popover border border-border/50 shadow-sm whitespace-nowrap pointer-events-none z-10",
            isInput ? "right-full mr-3" : "left-full ml-3",
          )}
        >
          <span
            className={cn(
              "text-[10px] leading-none font-medium",
              info.textClass,
              !required && "opacity-80",
            )}
          >
            {label ?? name.replace(/_/g, " ")}
          </span>
        </div>
      )}
    </div>
  );
}
